/**
 * The spread model and its derived statistics.
 *
 * `analyseSpread` is the one place coverage, stake, net-per-pocket, expected
 * value and ring gaps are computed. The UI, the analysis panel and the worker
 * all read from it, so the numbers on screen can never disagree.
 */

import type {
  BetType,
  OddsBreakdown,
  OddsTier,
  PlacedBet,
  PocketCoverage,
  PocketId,
  Range,
  RingGap,
  Spread,
  SpreadAnalysis,
  TableProfile,
  WheelDefinition,
  WheelStyle,
} from './types';
import { betKey, coveredPockets, isValidBet } from './bets';
import {
  defaultTableFor,
  payoutFor,
  stakeCents,
  tableSignature,
  zeroRefundCents,
} from './profiles';
import { getWheel } from './wheels';

// ---------------------------------------------------------------------------
// Construction and editing
// ---------------------------------------------------------------------------

export function newSpreadId(): string {
  return `sp_${Date.now().toString(36)}_${Math.floor(Math.random() * 0xffffff).toString(36)}`;
}

export function createSpread(name: string, wheelStyle: WheelStyle, bets: PlacedBet[] = []): Spread {
  const now = Date.now();
  return { id: newSpreadId(), name, wheelStyle, bets, tags: [], createdAt: now, updatedAt: now };
}

/** Merge same-target bets, drop non-positive stakes, and order deterministically. */
export function normaliseBets(bets: readonly PlacedBet[]): PlacedBet[] {
  const merged = new Map<string, PlacedBet>();
  for (const bet of bets) {
    const key = betKey(bet);
    const existing = merged.get(key);
    merged.set(key, existing ? { ...existing, chips: existing.chips + bet.chips } : { ...bet });
  }
  return [...merged.values()]
    .filter((bet) => bet.chips > 0)
    .sort((a, b) => betKey(a).localeCompare(betKey(b)));
}

/** Add chips to a bet, merging with an identical existing bet. */
export function addBet(bets: readonly PlacedBet[], bet: PlacedBet): PlacedBet[] {
  return normaliseBets([...bets, bet]);
}

/** Remove `chips` from a matching bet; removes the bet entirely when it hits zero. */
export function removeChips(
  bets: readonly PlacedBet[],
  target: Pick<PlacedBet, 'betType' | 'target'>,
  chips: number,
): PlacedBet[] {
  const key = betKey(target);
  return normaliseBets(
    bets.map((bet) => (betKey(bet) === key ? { ...bet, chips: bet.chips - chips } : bet)),
  );
}

export function removeBet(
  bets: readonly PlacedBet[],
  target: Pick<PlacedBet, 'betType' | 'target'>,
): PlacedBet[] {
  const key = betKey(target);
  return bets.filter((bet) => betKey(bet) !== key);
}

export function chipsOn(
  bets: readonly PlacedBet[],
  target: Pick<PlacedBet, 'betType' | 'target'>,
): number {
  const key = betKey(target);
  return bets.reduce((sum, bet) => (betKey(bet) === key ? sum + bet.chips : sum), 0);
}

/** Bets that are illegal on the given style — surfaced rather than silently dropped. */
export function invalidBets(bets: readonly PlacedBet[], style: WheelStyle): PlacedBet[] {
  return bets.filter((bet) => !isValidBet(bet, style));
}

// ---------------------------------------------------------------------------
// Ring gaps
// ---------------------------------------------------------------------------

/**
 * Runs of consecutive uncovered pockets on the physical ring, wrapping around.
 * The key wheel-side diagnostic: a spread can look broad on the felt and still
 * leave a third of the ring bare.
 */
export function findRingGaps(
  wheel: WheelDefinition,
  isCovered: (pocketId: PocketId) => boolean,
): RingGap[] {
  const n = wheel.pocketCount;
  const covered = wheel.ordered.map((pocket) => isCovered(pocket.id));

  if (covered.every(Boolean)) return [];
  if (covered.every((value) => !value)) {
    return [{ startIndex: 0, length: n, pockets: wheel.ordered.map((pocket) => pocket.id) }];
  }

  // Start walking from a covered pocket so wrapped runs are counted once.
  const origin = covered.indexOf(true);
  const gaps: RingGap[] = [];
  let runStart = -1;
  let runPockets: PocketId[] = [];

  for (let step = 0; step < n; step += 1) {
    const index = (origin + step) % n;
    if (!covered[index]) {
      if (runStart === -1) {
        runStart = index;
        runPockets = [];
      }
      const pocket = wheel.ordered[index];
      if (pocket) runPockets.push(pocket.id);
    } else if (runStart !== -1) {
      gaps.push({ startIndex: runStart, length: runPockets.length, pockets: runPockets });
      runStart = -1;
    }
  }
  if (runStart !== -1) {
    gaps.push({ startIndex: runStart, length: runPockets.length, pockets: runPockets });
  }
  return gaps;
}

// ---------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  if (sorted.length % 2 === 1) return sorted[mid] ?? 0;
  return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
}

function rangeOf(values: number[]): Range {
  return { min: Math.min(...values), max: Math.max(...values) };
}

/**
 * The §2.5 breakdown: stake grouped by payout tier at the active table, so the
 * user reads the shape of their stake distribution rather than a bet list.
 */
function computeOddsBreakdown(
  bets: readonly PlacedBet[],
  wheel: WheelDefinition,
  table: TableProfile,
  coverage: ReadonlyMap<PocketId, PocketCoverage>,
  netPerPocket: ReadonlyMap<PocketId, number>,
  totalStakeCents: number,
): OddsBreakdown {
  interface Accumulator {
    payout: number;
    betTypes: Set<BetType>;
    bets: number;
    chips: number;
    stakeCents: number;
    pockets: Set<PocketId>;
  }

  const byPayout = new Map<number, Accumulator>();
  for (const bet of bets) {
    const payout = payoutFor(table, bet.betType);
    let tier = byPayout.get(payout);
    if (!tier) {
      tier = { payout, betTypes: new Set(), bets: 0, chips: 0, stakeCents: 0, pockets: new Set() };
      byPayout.set(payout, tier);
    }
    tier.betTypes.add(bet.betType);
    tier.bets += 1;
    tier.chips += bet.chips;
    tier.stakeCents += stakeCents(table, bet.chips);
    for (const pocketId of coveredPockets(bet, wheel)) tier.pockets.add(pocketId);
  }

  const tiers: OddsTier[] = [...byPayout.values()]
    .sort((a, b) => b.payout - a.payout)
    .map((tier) => ({
      payout: tier.payout,
      betTypes: [...tier.betTypes].sort(),
      bets: tier.bets,
      chips: tier.chips,
      stakeCents: tier.stakeCents,
      stakeShare: totalStakeCents === 0 ? 0 : tier.stakeCents / totalStakeCents,
      pockets: tier.pockets.size,
      netIfHitCents: rangeOf([...tier.pockets].map((pocketId) => netPerPocket.get(pocketId) ?? 0)),
    }));

  // What a miss costs. Not simply −stake: under a zero rule a missed zero
  // refunds half of every even-money bet, so it costs less than a missed number.
  const uncovered = wheel.ordered
    .filter((pocket) => !coverage.has(pocket.id))
    .map((pocket) => netPerPocket.get(pocket.id) ?? 0);

  return {
    tiers,
    totalBets: bets.length,
    totalChips: bets.reduce((sum, bet) => sum + bet.chips, 0),
    totalStakeCents,
    pocketsCovered: coverage.size,
    pocketCount: wheel.pocketCount,
    netIfNoneHitCents: uncovered.length === 0 ? null : rangeOf(uncovered),
  };
}

function computeAnalysis(
  bets: readonly PlacedBet[],
  style: WheelStyle,
  table: TableProfile,
): SpreadAnalysis {
  const wheel = getWheel(style);

  const coverage = new Map<PocketId, { chips: number; bets: PlacedBet[] }>();
  let totalStake = 0;

  for (const bet of bets) {
    totalStake += bet.chips;
    for (const pocketId of coveredPockets(bet, wheel)) {
      let entry = coverage.get(pocketId);
      if (!entry) {
        entry = { chips: 0, bets: [] };
        coverage.set(pocketId, entry);
      }
      entry.chips += bet.chips;
      entry.bets.push(bet);
    }
  }

  const totalStakeCents = stakeCents(table, totalStake);
  const covers = new Map<string, Set<PocketId>>();
  for (const bet of bets) covers.set(betKey(bet), new Set(coveredPockets(bet, wheel)));

  // Net if a pocket hits, in cents: winners return stake + payout, losers lose
  // their stake — except an even-money bet on a zero under a partage rule,
  // which gets half back.
  const netPerPocket = new Map<PocketId, number>();
  let worstPocket: PocketId | null = null;
  let bestPocket: PocketId | null = null;
  let worstNet = Number.POSITIVE_INFINITY;
  let bestNet = Number.NEGATIVE_INFINITY;
  let netTotal = 0;

  for (const pocket of wheel.ordered) {
    let returned = 0;
    for (const bet of bets) {
      const stake = stakeCents(table, bet.chips);
      if (covers.get(betKey(bet))?.has(pocket.id)) {
        returned += bet.chips * (payoutFor(table, bet.betType) + 1) * table.baseUnit;
      } else if (pocket.isZero) {
        returned += zeroRefundCents(table, bet.betType, stake);
      }
    }
    const net = returned - totalStakeCents;

    netPerPocket.set(pocket.id, net);
    netTotal += net;
    if (net < worstNet) {
      worstNet = net;
      worstPocket = pocket.id;
    }
    if (net > bestNet) {
      bestNet = net;
      bestPocket = pocket.id;
    }
  }

  const coveredChips = [...coverage.values()].map((entry) => entry.chips);
  const pocketsCovered = coverage.size;
  const expectedValue = netTotal / wheel.pocketCount;
  const readonlyCoverage = coverage as ReadonlyMap<PocketId, PocketCoverage>;

  return {
    wheel,
    table,
    coverage: readonlyCoverage,
    totalStake,
    totalStakeCents,
    pocketsCovered,
    coveragePct: (pocketsCovered / wheel.pocketCount) * 100,
    netPerPocket,
    worstPocket: pocketsCovered === 0 ? null : worstPocket,
    bestPocket: pocketsCovered === 0 ? null : bestPocket,
    expectedValue,
    expectedValuePct: totalStakeCents === 0 ? 0 : expectedValue / totalStakeCents,
    oddsBreakdown: computeOddsBreakdown(
      bets,
      wheel,
      table,
      readonlyCoverage,
      netPerPocket,
      totalStakeCents,
    ),
    ringGaps: findRingGaps(wheel, (pocketId) => coverage.has(pocketId)),
    longestGap: null,
    medianChips: median(coveredChips),
    maxChips: coveredChips.length ? Math.max(...coveredChips) : 0,
  };
}

function withLongestGap(analysis: SpreadAnalysis): SpreadAnalysis {
  const longest = analysis.ringGaps.reduce<RingGap | null>(
    (best, gap) => (best === null || gap.length > best.length ? gap : best),
    null,
  );
  return { ...analysis, longestGap: longest };
}

// Memoisation. Solid stores mutate in place, so identity is not a safe key —
// a cheap content signature is.
const CACHE_LIMIT = 64;
const analysisCache = new Map<string, SpreadAnalysis>();

function signatureOf(
  bets: readonly PlacedBet[],
  style: WheelStyle,
  table: TableProfile,
): string {
  const parts = bets.map((bet) => `${bet.betType}:${bet.target}:${bet.chips}`).sort();
  return `${style}#${tableSignature(table)}#${parts.join(',')}`;
}

/**
 * The one place coverage, stake, net-per-pocket, expected value and the odds
 * breakdown are computed. Everything on screen reads from here, so the numbers
 * cannot disagree with each other.
 *
 * `table` defaults to the standard profile for the style, which is what makes
 * the app usable before anything is configured.
 */
export function analyseSpread(
  bets: readonly PlacedBet[],
  style: WheelStyle,
  table: TableProfile = defaultTableFor(style),
): SpreadAnalysis {
  const signature = signatureOf(bets, style, table);
  const cached = analysisCache.get(signature);
  if (cached) return cached;

  const analysis = withLongestGap(computeAnalysis(bets, style, table));
  if (analysisCache.size >= CACHE_LIMIT) {
    const oldest = analysisCache.keys().next().value;
    if (oldest !== undefined) analysisCache.delete(oldest);
  }
  analysisCache.set(signature, analysis);
  return analysis;
}

export function analyse(spread: Spread, table?: TableProfile): SpreadAnalysis {
  return analyseSpread(spread.bets, spread.wheelStyle, table);
}

/**
 * House edge for a spread at its table, as a signed fraction of stake.
 *
 * Identical to `expectedValuePct` — stated separately because the two must
 * always agree, and §9 turns on that. No arrangement of bets moves this number;
 * only the odds themselves do. With nothing staked there is no spread to
 * measure, so the wheel's own standard edge is returned as a placeholder.
 */
export function houseEdgeOf(analysis: SpreadAnalysis): number {
  return analysis.totalStake === 0 ? analysis.wheel.houseEdge : analysis.expectedValuePct;
}

/**
 * The standard-rules edge for a wheel style, for §9 to display *beside* the
 * computed figure. Never the source of a displayed result.
 */
export function standardHouseEdge(style: WheelStyle): number {
  return getWheel(style).houseEdge;
}
